import { useEffect, useRef } from 'react';

/**
 * Call `refresh` automatically when the page regains visibility/focus or when
 * the browser reconnects. Helps users see updated content (admin edits,
 * Premium plan changes, etc.) without a manual refresh.
 *
 * Throttles refreshes to at most one every `minIntervalMs` (default 8s) so we
 * don't hammer the API on tab-switching.
 */
export function useRevalidateOnFocus(refresh, { enabled = true, minIntervalMs = 8000 } = {}) {
  const lastRunRef = useRef(0);
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return undefined;

    const maybeRefresh = () => {
      if (typeof refreshRef.current !== 'function') return;
      const now = Date.now();
      if (now - lastRunRef.current < minIntervalMs) return;
      lastRunRef.current = now;
      try {
        refreshRef.current();
      } catch {
        /* swallow */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    const onFocus = () => maybeRefresh();
    const onOnline = () => maybeRefresh();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, minIntervalMs]);
}

export default useRevalidateOnFocus;
